import jwtDecode from 'jwt-decode';
import axios from 'src/utils/axios';
import moment from 'moment';
import { msalInstance, loginRequest } from '../authProvider';

class AuthService {
  setAxiosInterceptors = ({ onLogout }) => {
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          this.setSession(null);

          if (onLogout) {
            onLogout();
          }
        }

        return Promise.reject(error);
      }
    );
  };

  handleAuthentication() {
    this.getAccessToken().then((accessToken) => {
      if (!accessToken) {
        return;
      }

      if (this.isValidToken(accessToken)) {
        this.setSession(accessToken);
      } else {
        this.setSession(null);
      }
    });
  }

  loginWithEmailAndPassword = (email, password) => new Promise((resolve, reject) => {
    axios
      .post('Tokens/DashboardLogin', '', {
        headers: {
          Authorization: `Basic ${btoa(`${email}:${password}`)}`
        }
      })
      .then((response) => {
        const { data } = response;
        if (!data) {
          reject(new Error('Invalid Username or Password'));
        }

        this.setSession(data.token);
        localStorage.setItem('accessToken', data.token);
        localStorage.setItem('loggedInUser', JSON.stringify(data));
        resolve(data);
      })
      .catch((error) => {
        reject(error?.response?.data ?? error);
      });
  });

  loginInWithToken = () => new Promise((resolve, reject) => {
    const loggedInUser = JSON.parse(this.getLoggedInUser());

    if (!loggedInUser) {
      reject(new Error('Expired Token'));
    }

    resolve(loggedInUser);
  });

  logout = () => {
    this.setSession(null);
  };

  setSession = (accessToken) => {
    if (accessToken) {
      axios.defaults.headers.post['Content-Type'] = 'application/json';
      axios.defaults.headers.put['Content-Type'] = 'application/json';

      this.setCacheExpiry();
    } else {
      localStorage.removeItem('loggedInUser');
      localStorage.removeItem('accessToken');

      if (localStorage.getItem('azureUsername')) {
        localStorage.removeItem('azureUsername');
        msalInstance.logout({
          onRedirectNavigate: () => false
        });
      }
    }
  };

  setCacheExpiry = () => {
    const expiry = JSON.parse(localStorage.getItem('cacheExpiry'));
    if (expiry && moment(expiry) > moment()) {
      return;
    }

    localStorage.setItem(
      'cacheExpiry',
      JSON.stringify(
        moment()
          .add(15, 'minutes')
          .format()
      )
    );
  };

  getAccessToken = async () => {
    const username = localStorage.getItem('azureUsername');
    if (!username) {
      return localStorage.getItem('accessToken');
    }

    const account = msalInstance.getAccountByUsername(username);
    if (!account) {
      return undefined;
    }

    try {
      const token = await msalInstance.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account
      });

      return token.accessToken;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  };

  getDevopsToken = async () => {
    const username = localStorage.getItem('azureUsername');
    if (!username) {
      return undefined;
    }

    const account = msalInstance.getAccountByUsername(username);
    if (!account) {
      return undefined;
    }

    try {
      const token = await msalInstance.acquireTokenSilent({
        scopes: ['499b84ac-1321-427f-aa17-267ca6975798/user_impersonation'],
        account
      });

      return token.accessToken;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  };

  getLoggedInUser = () => localStorage.getItem('loggedInUser');

  isValidToken = (accessToken) => {
    if (!accessToken) {
      return false;
    }

    const decoded = jwtDecode(accessToken);
    const currentTime = Date.now() / 1000;

    return decoded.exp > currentTime;
  };

  isAuthenticated = async () => !!this.getAccessToken();
}

const authService = new AuthService();

export default authService;
